import * as React from 'react';

/**
 * 后台主题：浅色 / 深色 / 跟随系统，默认浅色，记在本机（localStorage）。
 * 深色时在 <html> 上挂 dark（弹层 portal 到 body 也跟着变），浅色不挂类（styles.css 的 :root 就是浅色）。
 * 首屏前由 public/theme-init.js 先按同一个键挂好类，避免闪一下；两处的键与默认值要保持一致。
 */
export type ThemePref = 'light' | 'dark' | 'system';
export const THEME_PREFS: ThemePref[] = ['light', 'dark', 'system'];
export const THEME_LABEL: Record<ThemePref, string> = { light: '浅色', dark: '深色', system: '跟随系统' };

const KEY = 'aitoken.admin-theme';
const listeners = new Set<() => void>();

function read(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'system' || v === 'light' ? v : 'light';
  } catch {
    return 'light';
  }
}

let current: ThemePref = typeof window === 'undefined' ? 'light' : read();

export function getThemePref(): ThemePref {
  return current;
}

export function setThemePref(p: ThemePref) {
  current = p;
  try {
    localStorage.setItem(KEY, p);
  } catch {
    /* 隐私模式 */
  }
  for (const l of listeners) l();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  // 其他标签页改了偏好，这里跟着变
  const onStorage = (e: StorageEvent) => {
    if (e.key !== KEY) return;
    current = read();
    fn();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(fn);
    window.removeEventListener('storage', onStorage);
  };
}

export function useThemePref(): ThemePref {
  return React.useSyncExternalStore(subscribe, getThemePref, () => 'light' as ThemePref);
}

const systemDark = () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

/** 在应用根部调用一次：按偏好给 <html> 挂 / 撤 dark，同步 theme-color；跟随系统时监听系统切换 */
export function useAdminTheme() {
  const pref = useThemePref();
  const [sysDark, setSysDark] = React.useState(systemDark);
  React.useEffect(() => {
    if (pref !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => setSysDark(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [pref]);
  const dark = pref === 'dark' || (pref === 'system' && sysDark);

  React.useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#14120f' : '#f7f8fa');
  }, [dark]);
  return dark;
}
